import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * A queue of pending deletes, each with its own independent expiry timer — deleting a
 * second item never cancels the first's undo window. `onExpire` performs the real
 * delete when a timer fires; `onRestore` re-inserts the item locally on undo, or on a
 * failed `onExpire`. Timers are plain `setTimeout`s, so they keep running (and still
 * call `onExpire`) even if the owning component unmounts; `mountedRef` only guards
 * against calling `setPending`/`onRestore` after that point.
 */
export default function useUndoQueue({ onExpire, onRestore, delay = 5000 }) {
    const [pending, setPending] = useState(new Map())
    const mountedRef = useRef(true)

    useEffect(
        () => () => {
            mountedRef.current = false
        },
        []
    )

    const requestDelete = useCallback(
        (note) => {
            const timeoutId = setTimeout(async () => {
                if (mountedRef.current) {
                    setPending((prev) => {
                        const next = new Map(prev)
                        next.delete(note._id)
                        return next
                    })
                }
                try {
                    await onExpire(note)
                } catch (e) {
                    console.error(e)
                    if (mountedRef.current) onRestore(note, e)
                }
            }, delay)
            setPending((prev) => {
                const next = new Map(prev)
                next.set(note._id, { note, timeoutId })
                return next
            })
        },
        [onExpire, onRestore, delay]
    )

    const undo = useCallback(
        (id) => {
            setPending((prev) => {
                const entry = prev.get(id)
                if (!entry) return prev
                clearTimeout(entry.timeoutId)
                onRestore(entry.note)
                const next = new Map(prev)
                next.delete(id)
                return next
            })
        },
        [onRestore]
    )

    const undoAll = useCallback(() => {
        setPending((prev) => {
            prev.forEach((entry) => {
                clearTimeout(entry.timeoutId)
                onRestore(entry.note)
            })
            return new Map()
        })
    }, [onRestore])

    return { pending, requestDelete, undo, undoAll }
}
